"use client";

import { Collection } from "react-aria-components/Collection";
import type { Selection } from "react-aria-components";
import { toast } from "@heroui/react";
import * as React from "react";
import {
  IconDotsVertical as MoreVertical,
  IconFolderPlus as FolderPlus,
  IconPencil as Edit,
  IconTrash as Trash2,
  IconPlus as Plus,
} from "@tabler/icons-react";
import { AlertDialog, Button, cn, Dropdown, Label } from "@heroui/react";
import { FileTree, useFileTree } from "@heroui-pro/react";
import { trpc } from "@/lib/trpc";
import { buildTree } from "@hs/shared";
import type { TreeSelectNode } from "@/components/custom";
import {
  CategoryEditDialog,
  type CategoryEditMode,
  type CategoryFlatItemForTree,
} from "@/pages/assets/components/CategoryEditDialog";
import type { KnowledgeBasesScope } from "../../components/KnowledgeBaseHeader";
import {
  KNOWLEDGE_BASE_CATEGORY_TYPE,
  KNOWLEDGE_BASE_SIDEBAR_ALL_ID,
  KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID,
} from "../../components/constants";
import { RacDialogTriggerAnchor } from "@/components/RacDialogTriggerAnchor";

type CategoryItem = { id: string; name?: string | null; parentId?: string | null; order?: number };

interface CategorySidebarNode extends TreeSelectNode {
  category: CategoryItem;
}

function findInCategoryTree(nodes: CategorySidebarNode[], id: string): CategorySidebarNode | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const hit = findInCategoryTree(n.children as CategorySidebarNode[], id);
      if (hit) return hit;
    }
  }
  return undefined;
}

function CategoryActionsMenu({
  category,
  onAddChild,
  onEdit,
  onDelete,
}: {
  category: CategoryItem;
  onAddChild?: (c: CategoryItem) => void;
  onEdit?: (c: CategoryItem) => void;
  onDelete?: (c: CategoryItem) => void;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div
      className={cn(
        "absolute end-0 top-1/2 z-10 flex -translate-y-1/2 items-center pe-0.5",
        "opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
        menuOpen && "opacity-100"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown isOpen={menuOpen} onOpenChange={setMenuOpen}>
        <Button
          isIconOnly
          aria-label="操作"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground !h-6 !min-h-6 !w-6 !min-w-6 !p-0"
        >
          <MoreVertical className="size-3.5" stroke={1.5} />
        </Button>
        <Dropdown.Popover className="min-w-[160px]" placement="bottom end">
          <Dropdown.Menu>
            <Dropdown.Item id="add-child" textValue="添加下级分类" onAction={() => onAddChild?.(category)}>
              <Label className="flex items-center gap-2">
                <FolderPlus className="size-4" stroke={1.5} />
                添加下级分类
              </Label>
            </Dropdown.Item>
            <Dropdown.Item id="edit" textValue="编辑" onAction={() => onEdit?.(category)}>
              <Label className="flex items-center gap-2">
                <Edit className="size-4" stroke={1.5} />
                编辑
              </Label>
            </Dropdown.Item>
            <Dropdown.Item id="delete" textValue="删除" variant="danger" onAction={() => onDelete?.(category)}>
              <Label className="flex items-center gap-2">
                <Trash2 className="size-4" stroke={1.5} />
                删除
              </Label>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </div>
  );
}

function isSidebarSystemNode(id: string) {
  return id === KNOWLEDGE_BASE_SIDEBAR_ALL_ID || id === KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID;
}

function renderCategoryTreeItem(
  item: CategorySidebarNode,
  ctx: {
    onAddChild: (c: CategoryItem) => void;
    onEdit: (c: CategoryItem) => void;
    onDelete: (c: CategoryItem) => void;
  }
) {
  const hasKids = !!(item.children && item.children.length > 0);
  const system = isSidebarSystemNode(item.id);
  const titleNode = (
    <span className={cn("relative flex min-w-0 flex-1 items-center", system ? "pe-1" : "pe-7")}>
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {!system ? (
        <CategoryActionsMenu
          category={item.category}
          onAddChild={ctx.onAddChild}
          onEdit={ctx.onEdit}
          onDelete={ctx.onDelete}
        />
      ) : null}
    </span>
  );

  return (
    <FileTree.Item className="group" dragIcon={false} id={item.id} textValue={item.label} title={titleNode}>
      {hasKids ? (
        <Collection items={item.children as CategorySidebarNode[]}>
          {(child) => renderCategoryTreeItem(child as CategorySidebarNode, ctx)}
        </Collection>
      ) : null}
    </FileTree.Item>
  );
}

export interface KbCategorySidebarProps {
  scope: KnowledgeBasesScope;
  selectedCategoryId: string | null;
  onCategorySelect: (categoryId: string | null, categoryName?: string | null) => void;
}

export function KbCategorySidebar({
  scope,
  selectedCategoryId,
  onCategorySelect,
}: KbCategorySidebarProps) {
  const categoryScope = scope === "my" ? "MINE" : "SPACE";
  const categoryTreeInput = React.useMemo(
    () => ({ type: KNOWLEDGE_BASE_CATEGORY_TYPE, scope: categoryScope }),
    [categoryScope]
  );
  const { data: categoryFlatList = [], isLoading: categoryTreeLoading, refetch: refetchCategoryTree } =
    trpc.categories.getCategoryTree.useQuery(categoryTreeInput as never, { staleTime: 0 });

  const handleCategoryDialogSuccess = React.useCallback(async () => {
    await refetchCategoryTree();
  }, [refetchCategoryTree]);

  const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
  const [categoryDialogMode, setCategoryDialogMode] = React.useState<CategoryEditMode>("create");
  const [editingCategory, setEditingCategory] = React.useState<CategoryItem | null>(null);
  const [parentCategoryForAddChild, setParentCategoryForAddChild] = React.useState<CategoryItem | null>(null);

  const handleEditCategory = React.useCallback((category: CategoryItem) => {
    setEditingCategory(category);
    setParentCategoryForAddChild(null);
    setCategoryDialogMode("update");
    setCategoryDialogOpen(true);
  }, []);

  const handleAddCategory = React.useCallback(() => {
    setEditingCategory(null);
    setParentCategoryForAddChild(null);
    setCategoryDialogMode("create");
    setCategoryDialogOpen(true);
  }, []);

  const handleAddChildCategory = React.useCallback((category: CategoryItem) => {
    setEditingCategory(null);
    setParentCategoryForAddChild(category);
    setCategoryDialogMode("add-child");
    setCategoryDialogOpen(true);
  }, []);

  const [categoryToDelete, setCategoryToDelete] = React.useState<CategoryItem | null>(null);
  const deleteCategoryMutation = trpc.categories.delete.useMutation({
    onSuccess: (_data, variables) => {
      toast.success("分类已删除");
      setCategoryToDelete(null);
      if (selectedCategoryId === variables.id) {
        onCategorySelect(KNOWLEDGE_BASE_SIDEBAR_ALL_ID, "全部");
      }
      refetchCategoryTree();
    },
  });

  const handleDeleteCategory = React.useCallback((category: CategoryItem) => {
    setCategoryToDelete(category);
  }, []);

  const handleConfirmDeleteCategory = React.useCallback(() => {
    if (categoryToDelete?.id) {
      deleteCategoryMutation.mutate({ id: categoryToDelete.id });
    }
  }, [categoryToDelete, deleteCategoryMutation]);

  const categoryCtx = React.useMemo(
    () => ({
      onAddChild: handleAddChildCategory,
      onEdit: handleEditCategory,
      onDelete: handleDeleteCategory,
    }),
    [handleAddChildCategory, handleEditCategory, handleDeleteCategory]
  );

  const categoryFileItems: CategorySidebarNode[] = React.useMemo(() => {
    const flat = (categoryFlatList as CategoryItem[]).filter((c) => c.id != null);
    if (flat.length === 0) return [];
    const tree = buildTree(flat, {
      parentField: "parentId",
      childrenField: "children",
      sortBy: "order",
      sortOrder: "asc",
    }) as { id: string; name?: string | null; parentId?: string | null; children?: unknown[] }[];
    const convert = (nodes: typeof tree): CategorySidebarNode[] =>
      nodes.map((node) => ({
        id: node.id,
        label: node.name ?? "",
        category: { id: node.id, name: node.name, parentId: node.parentId },
        children: node.children?.length ? convert(node.children as typeof tree) : undefined,
      }));
    return convert(tree);
  }, [categoryFlatList]);

  const rootSystemNodes = React.useMemo<CategorySidebarNode[]>(
    () => [
      {
        id: KNOWLEDGE_BASE_SIDEBAR_ALL_ID,
        label: "全部",
        category: { id: KNOWLEDGE_BASE_SIDEBAR_ALL_ID, name: "全部" },
      },
      {
        id: KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID,
        label: "未分类",
        category: { id: KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID, name: "未分类" },
      },
    ],
    []
  );

  const fileTreeItems = React.useMemo(
    () => [...rootSystemNodes, ...categoryFileItems],
    [rootSystemNodes, categoryFileItems]
  );

  const { expandableKeys } = useFileTree({ items: fileTreeItems });

  const selectedKeys = React.useMemo<Selection>(() => {
    if (selectedCategoryId) {
      return new Set([selectedCategoryId]);
    }
    return new Set([KNOWLEDGE_BASE_SIDEBAR_ALL_ID]);
  }, [selectedCategoryId]);

  const handleTreeSelection = React.useCallback(
    (keys: Selection) => {
      if (keys === "all" || keys.size === 0) {
        onCategorySelect(KNOWLEDGE_BASE_SIDEBAR_ALL_ID, "全部");
        return;
      }
      const id = [...keys][0] as string;
      if (id === KNOWLEDGE_BASE_SIDEBAR_ALL_ID) {
        onCategorySelect(KNOWLEDGE_BASE_SIDEBAR_ALL_ID, "全部");
        return;
      }
      if (id === KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID) {
        onCategorySelect(KNOWLEDGE_BASE_SIDEBAR_UNCATEGORIZED_ID, "未分类");
        return;
      }
      const node = findInCategoryTree(categoryFileItems, id);
      if (!node) return;
      onCategorySelect(node.id, node.label ?? null);
    },
    [categoryFileItems, onCategorySelect]
  );

  return (
    <>
      <div className="bg-sidebar flex h-full w-64 shrink-0 flex-col border-r border-[var(--border)]">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-[var(--border)] px-4">
            <span className="text-muted-foreground text-xs font-medium">按分类查看</span>
            <Button
              isIconOnly
              aria-label="新建分类"
              size="sm"
              variant="ghost"
              className="!h-6 !min-h-6 !w-6 !min-w-6 !p-0"
              onPress={handleAddCategory}
            >
              <Plus className="size-4" stroke={1.5} />
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {categoryTreeLoading ? (
              <div className="text-muted-foreground mb-2 text-center text-xs">分类加载中…</div>
            ) : null}
            {!categoryTreeLoading && categoryFileItems.length === 0 ? (
              <div className="text-muted-foreground mb-2 text-center text-xs">暂无自定义分类</div>
            ) : null}
            <FileTree
              aria-label="知识库分类"
              className="[&_[data-slot=file-tree-icon]]:hidden [&_[data-slot=file-tree-item]]:mb-1"
              defaultExpandedKeys={expandableKeys}
              items={fileTreeItems}
              selectedKeys={selectedKeys}
              selectionBehavior="replace"
              selectionMode="single"
              showGuideLines="hover"
              size="md"
              onSelectionChange={handleTreeSelection}
            >
              {(item) => renderCategoryTreeItem(item as CategorySidebarNode, categoryCtx)}
            </FileTree>
          </div>
        </div>
      </div>

      <AlertDialog isOpen={!!categoryToDelete} onOpenChange={(open) => !open && setCategoryToDelete(null)}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除「{categoryToDelete?.name ?? "该"}」分类吗？删除后不会删除分类内的知识库条目。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setCategoryToDelete(null)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteCategoryMutation.isPending}
                  onPress={handleConfirmDeleteCategory}
                >
                  {deleteCategoryMutation.isPending ? "删除中…" : "确定删除"}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <CategoryEditDialog
        open={categoryDialogOpen}
        onOpenChange={setCategoryDialogOpen}
        mode={categoryDialogMode}
        scope={scope}
        categoryTypeForCreate={KNOWLEDGE_BASE_CATEGORY_TYPE}
        categoryData={editingCategory ?? undefined}
        parentCategory={parentCategoryForAddChild ?? undefined}
        categoryFlatListFromParent={categoryFlatList as CategoryFlatItemForTree[]}
        onSuccess={handleCategoryDialogSuccess}
      />
    </>
  );
}
